/**
 * `src/lib/ui/type/scale.ts` — the reading-size and leading controls, and the
 * codec that fits both of them into the one frozen settings key there is.
 *
 * PURE. No DOM, no Dexie, no imports outside the frozen schema's types. The
 * application half is `apply.ts`; the persistence half is
 * `src/lib/ui/theme/settings.ts`.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  WHY BOTH AXES LIVE IN ONE NUMBER
 * ═══════════════════════════════════════════════════════════════════════════
 * SPEC §1.7 M2 requires reading size AND line height to be user-adjustable and
 * persisted. `src/lib/db/schema.ts` is a FROZEN CONTRACT — byte-compared against
 * the `frozen-contracts-v1` tag by `npm run verify:frozen`, which runs first in
 * `npm run verify` — and the only typographic keys it declares are:
 *
 *     'ui.type_scale': number
 *     'ui.theme':      'system' | 'light' | 'dark'
 *
 * There is no `ui.line_height`, and adding one means editing the frozen file.
 * So the two axes are PACKED into `ui.type_scale`:
 *
 *     value = sizeIndex + leadingIndex / 8
 *
 *     sizeIndex     integer 0…8   — nine reading sizes
 *     leadingIndex  integer −3…+3 — seven leadings, 0 in the middle
 *
 * Four properties make this a codec rather than a hack, and all four are
 * property-tested in `scale.spec.ts`:
 *
 *   1. LOSSLESS AND EXACT. Every leading offset is a multiple of 1/8, which is
 *      exact in binary64. There is no rounding drift, no `0.30000000000000004`,
 *      and `decode(encode(s, l))` is the identity for all 63 combinations.
 *   2. UNAMBIGUOUS. |leadingIndex / 8| ≤ 0.375 < 0.5, so `Math.round(value)`
 *      recovers `sizeIndex` for every legal value. Using ±0.5 would have made
 *      8.5 mean both (8, +4) and (9, −4); the table stops at ±3 for that reason
 *      and not for want of a fourth step.
 *   3. THE SHIPPED DEFAULT IS ALREADY VALID. `SETTING_DEFAULTS['ui.type_scale']`
 *      is `1`, which decodes to size index 1 and leading index 0 — the centre of
 *      the leading range and the default reading size. A device that has never
 *      opened the settings screen, and a device whose IndexedDB read failed,
 *      both render the same thing.
 *   4. TOTAL. `decode` accepts `unknown`, because settings values are `unknown`
 *      at rest and a row can hold anything an older build or a corrupt import
 *      wrote. Anything it cannot make sense of becomes the default rather than
 *      `NaN` in a `calc()`, which would silently collapse the whole type scale.
 *
 * It is still a packing, and it is worth saying so out loud: if the schema is
 * ever legitimately opened for a `version(2)` bump, `ui.line_height` should be
 * added and this codec retired, with `decode` kept for one release to read the
 * packed values already on disk. That is a migration note, not a TODO.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  AND WHY ONE SCALE MOVES EVERYTHING
 * ═══════════════════════════════════════════════════════════════════════════
 * DECISIONS D2: "The independent Arabic/Latin type-scale complexity in M2
 * collapses to one scale." One scale means the reading-size control multiplies
 * `--step--2 … --step-5` AND `--quran-size` by the same factor — chrome and
 * mushaf together. A control that grew only the scripture would be a second
 * scale wearing one setting's name.
 */

import { SETTING_DEFAULTS } from '$lib/db/schema';

/* ════════════════════════ the two tables ════════════════════════════════ */

export interface ReadingSize {
	/** Multiplier applied to every `--step-*` and to `--quran-size`. */
	readonly scale: number;
	/** Arabic label. The UI is Arabic-only (D2); there is nothing to translate. */
	readonly label: string;
}

/**
 * Nine reading sizes. Index 1 is the default because `SETTING_DEFAULTS` ships
 * `1` — so index 0 is the one step SMALLER than default and there are seven
 * larger. That asymmetry is deliberate and is the right way round: a mushaf
 * reader reaches for bigger text, and the 87.5% step exists for a dense
 * surah-index screen on a large display, not as the start of a downward ramp.
 */
export const READING_SIZES: readonly ReadingSize[] = [
	{ scale: 0.875, label: '٨٧٪' },
	{ scale: 1, label: '١٠٠٪' },
	{ scale: 1.125, label: '١١٢٪' },
	{ scale: 1.25, label: '١٢٥٪' },
	{ scale: 1.4, label: '١٤٠٪' },
	{ scale: 1.55, label: '١٥٥٪' },
	{ scale: 1.75, label: '١٧٥٪' },
	{ scale: 2, label: '٢٠٠٪' },
	{ scale: 2.25, label: '٢٢٥٪' }
];

/**
 * Seven leadings, as an offset added to `--quran-line-height` (and half of it to
 * body prose, a quarter to headings — see `app.css`).
 *
 * The mushaf base is 2.15, so the range is 1.79 … 2.51. The floor is not
 * arbitrary: below about 1.8, a fully-vocalised ayah in Amiri — fatha over
 * shadda over a superscript alef, with a Qur'anic pause mark beside it — starts
 * to touch the descenders of the line above. Amiri stacks marks vertically far
 * more than a Latin face stacks diacritics, which is why this control starts
 * where a Latin scale would have already stopped.
 */
export const LEADING_STEPS: readonly number[] = [-0.36, -0.24, -0.12, 0, 0.12, 0.24, 0.36];

/** `LEADING_STEPS[LEADING_ORIGIN]` is 0 — the default, and the centre. */
export const LEADING_ORIGIN = 3;

export const MIN_SIZE_INDEX = 0;
export const MAX_SIZE_INDEX = READING_SIZES.length - 1;
export const MIN_LEADING_INDEX = -LEADING_ORIGIN;
export const MAX_LEADING_INDEX = LEADING_STEPS.length - 1 - LEADING_ORIGIN;

/** The denominator of the packed fraction. Eight: a power of two, so exact. */
const LEADING_DENOMINATOR = 8;

/* ════════════════════════════ the codec ═════════════════════════════════ */

export interface TypeScale {
	/** 0 … 8, an index into `READING_SIZES`. */
	readonly sizeIndex: number;
	/** −3 … +3, an offset from `LEADING_ORIGIN` into `LEADING_STEPS`. */
	readonly leadingIndex: number;
}

export const DEFAULT_TYPE_SCALE: TypeScale = { sizeIndex: 1, leadingIndex: 0 };

function clamp(value: number, low: number, high: number): number {
	return value < low ? low : value > high ? high : value;
}

/** Pack a size and a leading into the single number `ui.type_scale` holds. */
export function encodeTypeScale(scale: TypeScale): number {
	const sizeIndex = clamp(Math.round(scale.sizeIndex), MIN_SIZE_INDEX, MAX_SIZE_INDEX);
	const leadingIndex = clamp(Math.round(scale.leadingIndex), MIN_LEADING_INDEX, MAX_LEADING_INDEX);
	return sizeIndex + leadingIndex / LEADING_DENOMINATOR;
}

/**
 * Unpack it. TOTAL: any `unknown` that is not a finite number — and any finite
 * number outside the legal range — becomes the default, because the alternative
 * is `NaN` reaching a `calc()` and silently zeroing the entire type scale.
 */
export function decodeTypeScale(value: unknown): TypeScale {
	if (typeof value !== 'number' || !Number.isFinite(value)) return DEFAULT_TYPE_SCALE;
	// |fraction| < 0.5 by construction, so rounding recovers the integer part
	// for every value this codec ever wrote. See property 2 in the header.
	const whole = Math.round(value);
	// `+ 0` normalises NEGATIVE ZERO. `Math.round(-0.375)` is `-0`, which is the
	// smallest reading size paired with the tightest leading — a real, reachable
	// combination — and `-0` would then leak out of the decoder into every
	// equality check the settings screen and the tests make. `Object.is(-0, 0)`
	// is false; `-0 + 0` is `+0`.
	const sizeIndex = clamp(whole, MIN_SIZE_INDEX, MAX_SIZE_INDEX) + 0;
	const leadingIndex =
		clamp(Math.round((value - whole) * LEADING_DENOMINATOR), MIN_LEADING_INDEX, MAX_LEADING_INDEX) +
		0;
	return { sizeIndex, leadingIndex };
}

/** The shipped default, read from the frozen schema rather than restated. */
export const SHIPPED_TYPE_SCALE: number = SETTING_DEFAULTS['ui.type_scale'];

/* ═════════════════════ what the stylesheet reads ════════════════════════ */

export interface TypeVariables {
	/** `--type-scale` */
	readonly '--type-scale': string;
	/** `--leading-adjust` */
	readonly '--leading-adjust': string;
}

/**
 * The two custom properties `app.css` reads. Nothing else in the app writes a
 * custom property onto the root element, so this function and `applyType` are
 * the whole seam between a stored number and a rendered page.
 */
export function typeVariables(scale: TypeScale): TypeVariables {
	const size = READING_SIZES[clamp(scale.sizeIndex, MIN_SIZE_INDEX, MAX_SIZE_INDEX)];
	const leading =
		LEADING_STEPS[clamp(scale.leadingIndex + LEADING_ORIGIN, 0, LEADING_STEPS.length - 1)];
	return {
		'--type-scale': String(size?.scale ?? 1),
		'--leading-adjust': String(leading ?? 0)
	};
}

/** The resolved mushaf line-height, for the reader's height cache and for the
 *  settings screen's live preview. `app.css` computes the same number in CSS;
 *  this is the same arithmetic in TypeScript, and `scale.spec.ts` pins them
 *  together against the stylesheet's own literals. */
export function quranLineHeight(scale: TypeScale, dark: boolean): number {
	const leading =
		LEADING_STEPS[clamp(scale.leadingIndex + LEADING_ORIGIN, 0, LEADING_STEPS.length - 1)];
	return QURAN_LINE_HEIGHT_BASE + (leading ?? 0) + (dark ? DARK_LEADING_BONUS : 0);
}

/** Must equal the `2.15` literal in `app.css`'s `--quran-line-height`. */
export const QURAN_LINE_HEIGHT_BASE = 2.15;
/** Must equal the `--leading-theme` the dark blocks in `app.css` set. */
export const DARK_LEADING_BONUS = 0.1;
